import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { List } from 'linqts';
import * as Moment from 'moment';
import AppRoutes from '../app-routes';
import { TaskReview, TimeBlock } from 'src/models/changeover-review';
import ChangeoverReview from 'src/models/changeover-review';
import LayoutHelper from './layout-helper';
import { ApiService } from '../services/api.service';
import { TimeBlockBottomSheetComponent, TimeBlockBottomSheetInput } from './time-block-bottom-sheet/time-block-bottom-sheet.component';
import { UserSessionService } from '../services/user-session.service';
import { FinaliseDialogComponent, FinaliseDialogComponentInput } from './finalise-dialog/finalise-dialog.component';
import { GanttComponent } from './gantt/gantt.component';
import { fromEvent, Subscription } from 'rxjs';
import { HammerGestureConfig } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { abbreviatedDurationHumanizer } from 'src/util/formatters';

export interface TimeDivision {
  value: number;
  label: string;
}

export interface LinkedChangeoverReview extends Omit<ChangeoverReview, 'tasks'> {
  tasks: Array<LinkedTaskReview>;
}

export interface LinkedTaskReview extends Omit<TaskReview, 'timeBlocks'> {
  timeBlocks: Array<LinkedTimeBlock>;
}

export interface LinkedTimeBlock extends TimeBlock {
  parentTask: TaskReview;
  previousInNavigation: LinkedTimeBlock | null;
  nextInNavigation: LinkedTimeBlock | null;
  precedingInTask: LinkedTimeBlock | null;
  followingInTask: LinkedTimeBlock | null;
}

@Component({
  selector: 'app-review',
  templateUrl: './review.component.html',
  styleUrls: ['./review.component.scss']
})
export class ReviewComponent implements OnInit, OnDestroy {
  showGestureCenter = false;
  gestureCenter: HammerPoint = { x: 0, y: 0 };

  model: LinkedChangeoverReview | null = null;
  modelFetchPromise: Promise<ChangeoverReview | null> | null = null;

  layoutHelper: LayoutHelper | null = null;
  timeDivisions: Array<TimeDivision> = [];

  selectedTimeBlock: LinkedTimeBlock | null = null;

  get canFinalise(): boolean {
    return !this.modelFetchPromise
      && !!this.model
      && this.model.tasks.length > 0
      && !this.model.isFinalised;
  }

  subscriptions = new Array<Subscription>();
  pinchInitialState: { xScale: number, xPosition: number } | null = null;

  private gestureContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('gestureContainer')
  set gestureContainerSetter(value: ElementRef<HTMLDivElement>) {
    this.gestureContainer = value;
    if (value) {
      this.addGestureHandlers();
    }
  };

  private horizontalScrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('horizontalScrollContainer')
  set horizontalScrollContainerSetter(value: ElementRef<HTMLDivElement>) {
    this.horizontalScrollContainer = value;

    if (value) {
      const element = value.nativeElement;
      element.scrollBy({
        behavior: 'auto',
        left: element.scrollWidth,
      });
    }
  };

  private gantt!: GanttComponent;
  @ViewChild('gantt')
  set ganttSetter(value: GanttComponent) { this.gantt = value; };

  @ViewChild('variableSizeContent')
  set variableSizeContentSetter(value: ElementRef<HTMLDivElement>) {
    // Auto-scale gantt chart
    if (value) {
      const currentScale = this.layoutHelper!.XScale;
      const newScale = (value.nativeElement.clientWidth / this.layoutHelper!.xUpperBound);
      if (newScale > currentScale) {
        setTimeout(() => this.layoutHelper!.XScale = newScale, 0);
      }
    }
  };

  constructor(
    private readonly bottomSheet: MatBottomSheet,
    private readonly api: ApiService,
    private readonly userSessionService: UserSessionService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
  ) {
    this.subscriptions.push(
      this.userSessionService.activeLineIdChanged.subscribe(lineId => this.loadData()),
    );
  }

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  addGestureHandlers(): void {
    const element = this.gestureContainer.nativeElement;
    const hammer = new HammerGestureConfig().buildHammer(element);
    this.subscriptions.push(
      fromEvent<MouseEvent>(element, 'mousewheel')
        .pipe(filter(event => !event.shiftKey))
        .subscribe(this.handleMouseWheelZoom),
      fromEvent<TouchInput>(hammer, 'pinchstart').subscribe(event => this.pinchInitialState = { xScale: this.layoutHelper!.XScale, xPosition: this.globalXToLocalX(event.center.x) }),
      fromEvent<TouchInput>(hammer, 'pinch').subscribe(this.handlePinchZoomAndPan),
      fromEvent<TouchInput>(hammer, 'pinchEnd').subscribe(_ => this.pinchInitialState = null),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.bottomSheet.dismiss();
  }

  @HostListener('gesturestart')
  preventGesturePropagation = (e: Event): void => e.preventDefault();

  @HostListener('click')
  clearSelection = (): void => this.gantt.clearSelection();

  public formatDuration = (minutes: number, largest?: number, spacer?: string): string =>
    abbreviatedDurationHumanizer(Moment.duration(minutes, 'm').asMilliseconds(), { largest: largest || 1, spacer: spacer !== undefined ? spacer : ' ' });

  get overrun(): number { return Math.max(0, this.model!.endToEndDurationInMinutes - this.model!.standardDurationInMinutes); }
  get overrunLabel(): string { return `over by ${this.formatDuration(this.overrun, 2, '')}`; }
  get underrun(): number { return Math.max(0, this.model!.standardDurationInMinutes - this.model!.endToEndDurationInMinutes); }
  get underrunLabel(): string { return `under by ${this.formatDuration(this.underrun, 2, '')}`; }

  get remainder(): number { return this.model!.endToEndDurationInMinutes - this.overrun; }

  get midMarker(): string { return this.formatDuration(Math.min(this.model!.endToEndDurationInMinutes, this.model!.standardDurationInMinutes), 2, ''); }
  get endMarker(): string { return this.formatDuration(Math.max(this.model!.endToEndDurationInMinutes, this.model!.standardDurationInMinutes), 2, ''); }

  needsNote = (task: TaskReview) =>
    ((task.onTaskTimeInMinutes - task.standardTimeInMinutes >= 2) ||
      (task.onTaskTimeInMinutes / task.standardTimeInMinutes) >= 1.2) &&
    !task.timeBlocks.some(block => block.operatorNoteReason !== null);

  private loadData = async (): Promise<void> => {
    try {
      this.layoutHelper = null;
      this.model = null;
      this.modelFetchPromise = this.api.getCurrentChangeoverReview(this.userSessionService.activeLineId)

      const rawModel = await this.modelFetchPromise;
      let updatedSelection: LinkedTimeBlock | null = null;

      if (rawModel) {
        rawModel.tasks
          .flatMap(task => task.timeBlocks.map((item, index, taskTimeBlocks) => {
            const preceding = taskTimeBlocks[index - 1] || null;
            const following = taskTimeBlocks[index + 1] || null;

            const result = item as LinkedTimeBlock;
            result.parentTask = task;
            result.precedingInTask = preceding as LinkedTimeBlock;
            result.followingInTask = following as LinkedTimeBlock;

            return result;
          }))
          .forEach((item, index, changeoverTimeBlocks) => {
            const previous = changeoverTimeBlocks[index - 1] || null;
            const next = changeoverTimeBlocks[index + 1] || null;

            item.previousInNavigation = previous;
            item.nextInNavigation = next;

            if (this.selectedTimeBlock?.startTaskEventId === item.startTaskEventId) {
              updatedSelection = item;
            }

            return item;
          });

        this.model = rawModel as LinkedChangeoverReview;
      }

      if (this.model) {
        this.layoutHelper = this.buildLayoutHelper(this.model);
      }

      this.onSelectionChanged(updatedSelection);

      this.modelFetchPromise = null;
    } catch {
      // do nothing for now
    }
  }

  public onSelectionChanged(selection: LinkedTimeBlock | null): void {
    this.selectedTimeBlock = selection;

    this.scrollIntoView(this.selectedTimeBlock);
    this.showBottomSheet(selection);
  }

  public onNavigatePrevious = (event: MouseEvent): void => {
    event.stopImmediatePropagation();
    const navigationTarget = this.selectedTimeBlock?.previousInNavigation;
    if (navigationTarget) {
      this.onSelectionChanged(navigationTarget);
    }
  };

  public onNavigateNext = (event: MouseEvent): void => {
    event.stopImmediatePropagation();
    const navigationTarget = this.selectedTimeBlock?.nextInNavigation || null;
    if (navigationTarget) {
      this.onSelectionChanged(navigationTarget);
    }
  }

  public finalise = async () => {
    const ongoingTasks = this.model!.tasks.filter(task => task.isOngoing);
    const tasksWithNotesNeeded = this.model!.tasks.filter(task => this.needsNote(task) || task.timeBlocks.some(block => GanttComponent.needsNote(block)));

    this.dialog.open<FinaliseDialogComponent, FinaliseDialogComponentInput, any>(
      FinaliseDialogComponent,
      {
        data: {
          changeoverId: this.model!.changeoverId,
          ongoingTasks: ongoingTasks.map(item => ({ id: item.taskId, name: item.name })),
          tasksWithNotesNeeded: tasksWithNotesNeeded.map(item => item.name),
          onSubmit: async () => {
            await Promise.all(
              ongoingTasks.map(task =>
                this.api.saveTaskEvent(
                  this.userSessionService.activeLineId,
                  this.model!.changeoverId,
                  task.taskId,
                  'Stop')));
            await this.api.finaliseChangeover(this.userSessionService.activeLineId, this.model!.changeoverId).catch(error => {
              if (error instanceof HttpErrorResponse && error.status == 400) {
                throw new Error(error.error);
              }
              throw error;
            });
            await this.router.navigate([AppRoutes.LineSchedule]);
          },
        }
      });
  }

  private buildLayoutHelper = (data: ChangeoverReview): LayoutHelper => {
    const layoutHelper = new LayoutHelper();

    layoutHelper.rowCount = data.tasks.length;

    const timeBlocks = data.tasks.flatMap(task => task.timeBlocks);

    if (timeBlocks.length === 0) {
      this.timeDivisions = [];
    } else {
      const minStartTime = Moment(
        new List(timeBlocks)
          .OrderBy(item => item.startOffsetInMinutes)
          .First().startTime);

      const indefiniteUpperBound =
        timeBlocks.some(item => item.isIndefinite) && Moment().diff(minStartTime, 'minutes');

      const definiteUpperBound =
        new List(timeBlocks)
          .Where(item => !item!.isIndefinite)
          .Select(item => item.endOffsetInMinutes)
          .OrderByDescending(item => item)
          .FirstOrDefault();

      const standardTimeUpperBound =
        new List(data.tasks)
          .Where(item => !item?.isOngoing)
          .Max(item => item.standardEndOffsetInMinutes || 0);

      let upperBound = indefiniteUpperBound || definiteUpperBound || layoutHelper.timeDivisionIncrement;
      if (standardTimeUpperBound > upperBound) {
        upperBound = standardTimeUpperBound;
      }

      if (upperBound % layoutHelper.timeDivisionIncrement !== 0) {
        upperBound = layoutHelper.timeDivisionIncrement * (1 + Math.floor(upperBound / layoutHelper.timeDivisionIncrement));
      }
      upperBound += layoutHelper.timeDivisionIncrement;

      const result = new Array<TimeDivision>();

      for (let t = 0; t < upperBound;) {
        const remainder = t % layoutHelper.timeDivisionIncrement;

        const block: TimeDivision = {
          value: t,
          label: this.formatDuration(t, 2, ''),
        };
        result.push(block);

        t += layoutHelper.timeDivisionIncrement - remainder;
      }

      const lastIndefiniteTimeBlock =
        new List(timeBlocks)
          .Where(item => item!.isIndefinite)
          .OrderBy(item => item.startOffsetInMinutes)
          .LastOrDefault() as TimeBlock | null;

      this.timeDivisions = result;
      layoutHelper.lowerBoundTime = minStartTime;
      layoutHelper.xUpperBound = upperBound;
      layoutHelper.lastIndefiniteTimeBlock = lastIndefiniteTimeBlock;
    }

    return layoutHelper;
  }

  globalXToLocalX = (globalX: number): number => globalX - this.horizontalScrollContainer.nativeElement.getBoundingClientRect().left + this.horizontalScrollContainer.nativeElement.scrollLeft;

  handleMouseWheelZoom = (event: any): void => {
    if (!this.layoutHelper) {
      return;
    }

    const localX = event.layerX;
    const newXScale = this.layoutHelper.XScale + (event.deltaY < 0 ? 0.5 : -0.5);

    const preScalePosition = localX / this.layoutHelper.XScale;
    const postScalePosition = localX / newXScale;
    const translation = (preScalePosition - postScalePosition) * newXScale;

    this.layoutHelper.XScale = newXScale;
    // only apply translation if scale has actually changed
    if (this.layoutHelper.XScale == newXScale) {
      this.horizontalScrollContainer.nativeElement.scrollBy(translation, 0);
    }
  }

  handlePinchZoomAndPan = (event: TouchInput): void => {
    if (this.showGestureCenter) {
      this.gestureCenter = event.center;
    }

    const localX = this.globalXToLocalX(event?.center?.x);
    const newXScale = (this.pinchInitialState?.xScale ?? 1) * event.scale;

    const newUnscaledPosition = localX / newXScale;
    const initialUnscaledPosition = this.pinchInitialState!?.xPosition / this.pinchInitialState!?.xScale;

    const unscaledTranslation = initialUnscaledPosition - newUnscaledPosition;
    const translation = unscaledTranslation * newXScale;

    this.layoutHelper!.XScale = newXScale;
    // only apply translation if scale has actually changed
    if (this.layoutHelper!.XScale === newXScale) {
      this.horizontalScrollContainer.nativeElement.scrollBy(translation, 0);
    }
  }

  private scrollIntoView(timeBlock: TimeBlock | null) {
    if (timeBlock && this.horizontalScrollContainer?.nativeElement) {
      const element = this.horizontalScrollContainer.nativeElement;
      const position = timeBlock.startOffsetInMinutes * this.layoutHelper!.XScale;

      const rightStickyColumnsWidth = 80 + 80;

      const viewportLeft = element.scrollLeft;
      const viewportRight = viewportLeft + element.clientWidth - rightStickyColumnsWidth;
      const isSufficientlyVisible = position > viewportLeft && position < (0.68 * viewportRight);

      if (!isSufficientlyVisible) {
        const proportionOfClient = 0.32;
        const target = position - (element.clientWidth * proportionOfClient);

        element.scroll({ behavior: 'smooth', left: target });
      }
    }
  }

  private showBottomSheet(selection: LinkedTimeBlock | null) {
    if (selection) {
      if (!this.bottomSheet._openedBottomSheetRef) {
        this.bottomSheet.open<TimeBlockBottomSheetComponent, TimeBlockBottomSheetInput>(
          TimeBlockBottomSheetComponent,
          {
            data: {
              timeBlock: selection,
              onTimeBlockEdited: this.loadData,
            },
            hasBackdrop: false,
          }
        );
      } else {
        const sheet = this.bottomSheet._openedBottomSheetRef.instance as TimeBlockBottomSheetComponent;
        sheet.update(selection);
      }
    }

    if (this.bottomSheet._openedBottomSheetRef && !selection) {
      this.bottomSheet.dismiss();
    }
  }
}
